import React, { useState, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import Content from "./components/Content";
import Login from "./components/Login";
import Sidebar from "./components/Sidebar";
import { getTheme } from "./styles/theme";
import "./styles/colors.css";
import "./styles/global.css";
import { ShipSettingsProvider } from "./Contexts/ShipSettingsContext"; // Importera ShipSettingsProvider
import { UserProvider } from "./Contexts/UserContext";
import { SettingsProvider } from "./Contexts/SettingsContext";
import { CompanyProvider } from "./Contexts/CompanyContext";
import FeedbackBanner from "./components/FeedbackBanner";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/sv";
import "./i18n";
import { VoyageProviderV2 } from "./Contexts/VoyageContextV2";
import { NotificationProvider } from "./Contexts/NotificationContext";
import { DocumentArchiveProvider } from "./Contexts/DocumentArchiveContext";
import {
  TenantBootstrapProvider,
  useTenantBootstrap,
} from "./Contexts/TenantBootstrapContext";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import {
  FoundationProvider,
  useFoundationBootstrap,
} from "./application/foundation";

const FoundationBootstrap = ({ children }) => {
  useFoundationBootstrap();
  return children;
};

const TenantBootstrapGate = ({ children }) => {
  const { status, phase, tenantBootstrap, error, errorMessage } =
    useTenantBootstrap();

  if (status === "ready") {
    return <React.Fragment key={tenantBootstrap?.tenantSlug}>{children}</React.Fragment>;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        background:
          "radial-gradient(circle at center, rgba(66,133,244,0.18) 0%, rgba(255,255,255,1) 100%)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: 4,
          borderRadius: 4,
          textAlign: "center",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {status === "loading" ? (
          <>
            <CircularProgress size={32} />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>
              {phase === "resolving"
                ? "Resolving tenant..."
                : "Connecting to customer..."}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {errorMessage}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {error?.details?.tenantSlug
                ? `Slug: ${error.details.tenantSlug}`
                : null}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

const App = () => {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("darkMode") === "true"
  );

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-mode");
      document.body.classList.remove("light-mode");
    } else {
      document.body.classList.add("light-mode");
      document.body.classList.remove("dark-mode");
    }

    // Spara darkMode i localStorage
    localStorage.setItem("darkMode", darkMode.toString());
  }, [darkMode]);

  useEffect(() => {
    // Blockera back-knappen och swipe-navigering
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.forward(); // Förhindrar navigering bakåt
    };

    // Se till att vi alltid är på senaste sidan
    window.history.replaceState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    // Stoppa sidomladdning (F5, Ctrl+R, högerklicksmeny)
    const blockReload = (event) => {
      if (event.key === "F5" || (event.ctrlKey && event.key === "r")) {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", blockReload);

    // Lägg till varning vid försök att lämna sidan
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "Är du säker på att du vill lämna sidan?";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Rensa event listeners vid avmontering
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("keydown", blockReload);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const [activeSection, setActiveSection] = useState("currentVoyage");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMobile] = useState(window.innerWidth <= 600);
  const [open, setOpen] = useState(!isMobile);

  const handleLogin = (userData) => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
  };

  const theme = getTheme(darkMode);

  return (
    <TenantBootstrapProvider>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sv">
          <TenantBootstrapGate>
            <FoundationProvider>
              <FoundationBootstrap>
                <NotificationProvider>
                  <SettingsProvider>
                    <UserProvider>
                      <CompanyProvider>
                        <ShipSettingsProvider>
                          <VoyageProviderV2>
                            <DocumentArchiveProvider>{isLoggedIn ? (
                              <>
                                <Sidebar
                                  onSelect={setActiveSection}
                                  onLogout={handleLogout}
                                  open={open}
                                  isMobile={isMobile}
                                  setOpen={setOpen}
                                  setDarkMode={setDarkMode}
                                  darkMode={darkMode}
                                />
                                <Content
                                  activeSection={activeSection}
                                  onSelect={setActiveSection}
                                  isMobile={isMobile}
                                  onLogout={handleLogout}
                                  open={open}
                                />
                                <FeedbackBanner />
                              </>
                            ) : (
                              <Login
                                onLogin={handleLogin}
                                setDarkMode={setDarkMode}
                                darkMode={darkMode}
                              />
                            )}</DocumentArchiveProvider>
                          </VoyageProviderV2>
                        </ShipSettingsProvider>
                      </CompanyProvider>
                    </UserProvider>
                  </SettingsProvider>
                </NotificationProvider>
              </FoundationBootstrap>
            </FoundationProvider>
          </TenantBootstrapGate>
        </LocalizationProvider>
      </ThemeProvider>
    </TenantBootstrapProvider>
  );
};

export default App;
